import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, CheckCircle2, ChevronRight, Circle, FileKey2, LockKeyhole, Server } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import type { CommercialAuthorization, Installation, LicenseSummary } from '../types'

const requestLabels = { NEW: '新购', RENEWAL: '续费', UPGRADE: '升级 / 扩容', DOWNGRADE: '降级' }
const editionLabels: Record<string, string> = {
  basic: '基础版', standard: '标准版', enterprise: '旗舰版',
  department: '基础版', group: '标准版', oem: '旗舰版', flagship: '旗舰版', custom: '旗舰版',
}
const architectureLabels: Record<Installation['deploymentArchitecture'], string> = {
  SINGLE_NODE: '单节点部署', CLUSTER: '集群部署',
}

export function IssueLicensePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const migrationWorkItemId = searchParams.get('migrationWorkItemId') || ''
  const migrationAuthorizationId = searchParams.get('authorizationId') || ''
  const sourceTaskId = searchParams.get('taskId') || ''
  const sourceLicenseId = searchParams.get('fromLicenseId') || ''
  const [authorizations, setAuthorizations] = useState<CommercialAuthorization[]>([])
  const [authorizationQuery, setAuthorizationQuery] = useState('')
  const [authorizationId, setAuthorizationId] = useState('')
  const [installations, setInstallations] = useState<Installation[]>([])
  const [plannedInstallationId, setPlannedInstallationId] = useState('')
  const [sourceLicense, setSourceLicense] = useState<LicenseSummary>()
  const [exceptionReason, setExceptionReason] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [draftReady, setDraftReady] = useState(false)
  const [draftRestored, setDraftRestored] = useState(false)
  const lastSubmission = useRef<{ signature: string, key: string } | undefined>(undefined)
  const draftKey = `license-application-draft:${sourceLicenseId || migrationWorkItemId || 'new'}`
  const selected = authorizations.find(item => item.authorizationId === authorizationId)
  const filteredAuthorizations = authorizations.filter(item => `${item.contractOrOrderNo} ${item.customerName} ${item.productName}`
    .toLocaleLowerCase('zh-CN').includes(authorizationQuery.trim().toLocaleLowerCase('zh-CN')))
  const visibleAuthorizations = selected && !filteredAuthorizations.some(item => item.authorizationId === selected.authorizationId)
    ? [selected, ...filteredAuthorizations] : filteredAuthorizations
  const eligibleInstallations = installations.filter(item => item.customerId === selected?.customerId
    && item.commercialAuthorizationId === authorizationId
    && !['SUSPENDED', 'DECOMMISSIONED'].includes(item.status))
  const selectedInstallation = eligibleInstallations.find(item => item.installationId === plannedInstallationId)
  const classification = useMemo(() => classifyRequest(sourceLicense, selected), [sourceLicense, selected])
  const readiness = [
    {
      label: '已选有效商业授权',
      ready: selected?.status === 'ACTIVE',
      detail: selected ? `${selected.contractOrOrderNo} 已复核，权益和期限将自动带入。` : '选择已复核的合同或订单。',
    },
    {
      label: '已固化计划安装实例',
      ready: Boolean(selectedInstallation),
      detail: selectedInstallation ? `${selectedInstallation.installationId} · ${architectureLabels[selectedInstallation.deploymentArchitecture]} 会随申请进入审批，签发时不能替换。` : '选择同一客户、同一商业授权下可用的目标实例。',
    },
    {
      label: '设备激活将在签发后办理',
      ready: true,
      detail: '本次只锁定目标实例；License 签发后按交付向导登记设备并完成激活回执。',
      informational: true,
    },
  ]

  useEffect(() => {
    const source = sourceLicenseId ? api.license(sourceLicenseId).catch(() => undefined as LicenseSummary | undefined)
      : Promise.resolve(undefined as LicenseSummary | undefined)
    Promise.all([api.availableCommercialAuthorizations(), source]).then(async ([items, original]) => {
      const draft = readDraft(draftKey)
      setDraftRestored(Boolean(draft.savedAt
        && (draft.authorizationId || draft.plannedInstallationId || draft.exceptionReason)))
      const originalAuthorization = original?.commercialAuthorizationId
        ? await api.commercialAuthorization(original.commercialAuthorizationId).catch(() => undefined)
        : undefined
      const eligibleItems = originalAuthorization
        ? items.filter(item => item.rootAuthorizationId === originalAuthorization.rootAuthorizationId)
        : items
      setAuthorizations(eligibleItems)
      setSourceLicense(await resolveIssuedSource(original))
      const requestedAuthorizationId = migrationAuthorizationId || original?.commercialAuthorizationId || draft.authorizationId
      const requested = eligibleItems.find(item => item.authorizationId === requestedAuthorizationId)
      // A regular request must be explicitly tied to a contract; selecting the first row can misfile it.
      setAuthorizationId(requested?.authorizationId || '')
      if (migrationWorkItemId && requested) {
        const workItems = await api.commercialAuthorizationMigrationWorkItems(requested.authorizationId)
        setPlannedInstallationId(workItems.find(item => item.workItemId === migrationWorkItemId)?.installationId || '')
      } else if (original?.plannedInstallationId) {
        setPlannedInstallationId(original.plannedInstallationId)
      } else if (draft.plannedInstallationId) {
        setPlannedInstallationId(draft.plannedInstallationId)
      }
      setExceptionReason(draft.exceptionReason || '')
      if (migrationWorkItemId && !requested) setError('迁移工作项指定的后继商业授权当前不可申请，请先处理授权状态或联系运营人员。')
      if (sourceLicenseId && !requested) {
        setError('原 License 对应的商业授权当前不可申请，请选择一份有效商业授权后继续。')
      }
    }).catch(value => setError(value instanceof Error ? value.message : '无法加载可用商业授权'))
      .finally(() => setDraftReady(true))
  }, [draftKey, migrationAuthorizationId, migrationWorkItemId, sourceLicenseId])

  useEffect(() => {
    if (!draftReady) return
    localStorage.setItem(draftKey, JSON.stringify({ authorizationId, plannedInstallationId, exceptionReason, savedAt: new Date().toISOString() }))
  }, [authorizationId, draftKey, draftReady, exceptionReason, plannedInstallationId])

  useEffect(() => {
    if (!authorizationId) { setInstallations([]); return }
    let active = true
    api.installations(authorizationId, 0, 100).then(result => {
      if (!active) return
      const matching = result.content.filter(item => item.commercialAuthorizationId === authorizationId
        && !['SUSPENDED', 'DECOMMISSIONED'].includes(item.status))
      setInstallations(matching)
      setPlannedInstallationId(current => matching.some(item => item.installationId === current) ? current : '')
    }).catch(value => { if (active) setError(value instanceof Error ? value.message : '无法加载计划安装实例') })
    return () => { active = false }
  }, [authorizationId])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSaving(true); setError('')
    try {
      const request = {
        commercialAuthorizationId: authorizationId,
        plannedInstallationId,
        exceptionReason: exceptionReason || undefined,
        sourceTaskId: sourceTaskId || undefined,
        sourceLicenseId: sourceLicenseId || undefined,
      }
      const signature = JSON.stringify(request)
      if (!lastSubmission.current || lastSubmission.current.signature !== signature) {
        lastSubmission.current = { signature, key: crypto.randomUUID() }
      }
      const created = migrationWorkItemId
        ? await api.requestCommercialAuthorizationMigrationLicense(migrationWorkItemId, request, lastSubmission.current.key)
        : await api.requestLicense(request, lastSubmission.current.key)
      localStorage.removeItem(draftKey)
      // Keep the requester in the delivery context instead of sending them back to a generic list.
      navigate(`/license-cases/${created.licenseId}`, { replace: true })
    } catch (value) { setError(value instanceof Error ? value.message : '申请提交失败') }
    finally { setSaving(false) }
  }

  return <form onSubmit={submit} className="issue-page">
    <div className="issue-toolbar"><Link to={migrationWorkItemId ? '/commercial-authorizations' : '/applications'} className="back-link"><ArrowLeft size={17} />{migrationWorkItemId ? '返回迁移看板' : '返回申请中心'}</Link><button className="button button--primary" disabled={saving || !authorizationId || !plannedInstallationId}><FileKey2 size={17} />{saving ? '正在提交...' : migrationWorkItemId ? '提交迁移审批' : '提交审批'}</button></div>
    {error && <div className="error-banner">{error}</div>}
    <div className="issue-layout"><div className="issue-form">
      {sourceLicenseId && <div className="inline-notice"><CheckCircle2 size={17} />正在基于原 License <code>{sourceLicenseId}</code> 发起新申请。系统会带入原商业授权和计划实例，并按新旧权益自动识别申请类型。</div>}
      {draftRestored && <div className="inline-notice"><CheckCircle2 size={17} />已自动恢复未提交草稿；提交成功后草稿会自动清除。</div>}
      <section className="form-section"><div className="section-index"><span>01</span><div><h3>{migrationWorkItemId ? '迁移后继商业授权' : '选择商业授权'}</h3><p>{migrationWorkItemId ? '该申请已绑定修订迁移工作项，不能改为其他商业授权。' : '仅显示已由审批人复核、处于有效期的合同或订单快照。'}</p></div></div>
        <div className="form-grid"><label className="span-2">查找合同、客户或产品<input value={authorizationQuery} disabled={!!migrationWorkItemId} onChange={event => setAuthorizationQuery(event.target.value)} placeholder="输入合同号、客户或产品名称" /></label><label className="span-2">有效合同 / 订单<select value={authorizationId} disabled={!!migrationWorkItemId} onChange={event => { setAuthorizationId(event.target.value); setPlannedInstallationId('') }} required><option value="">请选择商业授权</option>{visibleAuthorizations.map(item => <option key={item.authorizationId} value={item.authorizationId}>{item.contractOrOrderNo} · {item.customerName} · {item.productName} · 有效至 {item.endDate}</option>)}</select><small>{authorizationQuery.trim() ? `已匹配 ${filteredAuthorizations.length} 份可申请商业授权` : `共 ${authorizations.length} 份可申请商业授权`}</small></label></div>
        {!authorizations.length && <div className="inline-notice"><LockKeyhole size={17} /><span>暂无可申请的商业授权。请先补齐合同/订单并提交复核。 <Link to="/commercial-authorizations">去补齐合同</Link></span></div>}
      </section>
      {selected && <>
        <section className="form-section"><div className="section-index"><span>02</span><div><h3>购买快照</h3><p>此处内容由商业授权冻结，申请人不能编辑。</p></div></div>
          <div className="read-only-grid"><div><small>客户</small><b>{selected.customerName}</b></div><div><small>合同 / 订单</small><b>{selected.contractOrOrderNo}</b></div><div><small>产品 / 版本</small><b>{selected.productName} · {editionLabels[selected.edition] || selected.edition}</b></div><div><small>功能项</small><b>{selected.featureSnapshot.map(item => item.name).join('、')}</b></div><div><small>授权期限</small><b>{selected.startDate} 至 {selected.endDate}</b></div></div>
          <div className="snapshot-digest"><CheckCircle2 size={16} />权威快照 SHA-256：<code>{selected.snapshotSha256}</code></div>
        </section>
        <section className="form-section"><div className="section-index"><span>03</span><div><h3>选择计划安装实例</h3><p>目标实例会随申请进入审批证据，提交后不能在签发阶段更换。</p></div></div>
          <div className="form-grid"><label className="span-2">计划安装实例<select value={plannedInstallationId} disabled={!!migrationWorkItemId} onChange={event => setPlannedInstallationId(event.target.value)} required><option value="">请选择计划安装实例</option>{eligibleInstallations.map(item => <option key={item.installationId} value={item.installationId}>{item.installationId} · {architectureLabels[item.deploymentArchitecture]} · {item.environmentType} · {item.status}</option>)}</select><small>{migrationWorkItemId ? '迁移申请已锁定工作项中的实例。' : '仅显示属于同一客户、绑定当前商业授权且未停用/退役的实例；集群只选择集群级实例，不按节点重复申请。'}</small></label></div>
          {!eligibleInstallations.length && <div className="inline-notice"><Server size={16} /><span>当前商业授权下没有可用实例。 <Link to={`/installations?open=create&commercialAuthorizationId=${encodeURIComponent(authorizationId)}`}>登记计划安装实例</Link></span></div>}
          <p className="inline-notice"><LockKeyhole size={16} />提交后，计划实例将作为审批证据固化；目标变化时应撤回或驳回后重新申请，签发阶段不能临时换实例。</p>
        </section>
        <section className="form-section"><div className="section-index"><span>04</span><div><h3>系统识别申请类型</h3><p>申请类型由原 License 与当前商业授权的权益差异自动判定，申请人不能手工降低审批风险。</p></div></div>
          <div className="request-classification"><div><small>系统识别</small><b>{requestLabels[classification.type]}</b><p>{classification.detail}</p></div>{sourceLicense && <div><small>权益变化</small><b>{classification.removed.length ? `减少 ${classification.removed.length} 项` : classification.added.length ? `增加 ${classification.added.length} 项` : '权益不变'}</b><p>{[...classification.added.map(code => `新增 ${code}`), ...classification.removed.map(code => `移除 ${code}`)].join('；') || '主要变化为授权期限或合同修订。'}</p></div>}</div>
          <div className="form-grid"><label className="span-2">申请说明{classification.type === 'DOWNGRADE' ? '（必填）' : '（可选）'}<textarea value={exceptionReason} onChange={event => setExceptionReason(event.target.value)} maxLength={500} required={classification.type === 'DOWNGRADE'} placeholder={classification.type === 'DOWNGRADE' ? '请说明权益减少原因、客户确认情况和影响范围' : '如有交付窗口、客户背景或特殊情况，请在此说明'} /></label></div>
        </section>
      </>}
    </div><aside className="issue-summary"><div className="summary-sticky"><span className="section-kicker">提交前只需确认两项</span><h3>申请准备度</h3><p className="issue-readiness-intro">合同权益由系统冻结，目标实例由本次申请固化；无需手工填写 License 参数。</p><div className="issue-readiness-list">{readiness.map(item => <div className={item.ready ? (item.informational ? 'is-info' : 'is-ready') : 'needs-action'} key={item.label}>{item.ready ? <CheckCircle2 size={17} /> : <Circle size={17} />}<span><b>{item.label}</b><small>{item.detail}</small></span></div>)}</div><dl><div><dt>申请内容</dt><dd>权益快照 + 计划实例</dd></div><div><dt>申请类型</dt><dd>{requestLabels[classification.type]}</dd></div></dl><div className="signature-note"><LockKeyhole size={17} /><p>提交后计划实例不可修改。审批和签发都会核对同一实例，避免将 License 签发到未经审批的目标。</p></div><button className="button button--primary button--wide" disabled={saving || !authorizationId || !plannedInstallationId}>{saving ? '正在提交...' : <>提交并进入审批 <ChevronRight size={17} /></>}</button></div></aside></div>
  </form>
}

type RequestClassification = {
  type: keyof typeof requestLabels
  detail: string
  added: string[]
  removed: string[]
}

function classifyRequest(source?: LicenseSummary, authorization?: CommercialAuthorization): RequestClassification {
  if (!source || !authorization) return { type: 'NEW', detail: '首次为计划安装实例申请正式 License。', added: [], removed: [] }
  const previous = new Set(source.featureSnapshot.map(item => item.code))
  const requested = new Set(authorization.featureSnapshot.map(item => item.code))
  const added = [...requested].filter(code => !previous.has(code)).sort()
  const removed = [...previous].filter(code => !requested.has(code)).sort()
  const ranks: Record<string, number> = { basic: 1, department: 1, standard: 2, group: 2, enterprise: 3, flagship: 3, custom: 3, oem: 3 }
  if (removed.length || (ranks[authorization.edition] ?? 2) < (ranks[source.edition] ?? 2)) {
    return { type: 'DOWNGRADE', detail: '检测到权益或 Edition 减少，将自动进入特殊风险审批。', added, removed }
  }
  if (added.length || (ranks[authorization.edition] ?? 2) > (ranks[source.edition] ?? 2)
      || authorization.deploymentType !== source.deploymentType || authorization.productCode !== source.productCode) {
    return { type: 'UPGRADE', detail: '检测到权益、Edition、产品或部署方式增加/变更。', added, removed }
  }
  return { type: 'RENEWAL', detail: '核心权益不变，本次主要更新授权期限或合同修订。', added, removed }
}

async function resolveIssuedSource(source?: LicenseSummary): Promise<LicenseSummary | undefined> {
  let current = source
  const visited = new Set<string>()
  while (current && !current.issuedAt && current.sourceLicenseId) {
    if (visited.has(current.licenseId)) return undefined
    visited.add(current.licenseId)
    current = await api.license(current.sourceLicenseId).catch(() => undefined)
  }
  return current?.issuedAt ? current : undefined
}

function readDraft(key: string): { authorizationId?: string; plannedInstallationId?: string; exceptionReason?: string; savedAt?: string } {
  try { return JSON.parse(localStorage.getItem(key) || '{}') }
  catch { return {} }
}
